import { 
  Search,
  CreditCard,
  Calendar,
  Receipt,
  User,
  Clock,
  Sparkles
} from "lucide-react";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";
import dashboardWebPortal from "@/assets/dashboard_web_portal.png";

const portalFeatures = [
  { icon: Search, title: "Station Finder", description: "Interactive map with filters" },
  { icon: CreditCard, title: "Easy Payments", description: "Multiple payment methods" },
  { icon: Calendar, title: "Reservations", description: "Book charging slots ahead" },
  { icon: Receipt, title: "Session History", description: "Complete charging records" },
  { icon: User, title: "Profile Management", description: "Preferences and vehicles" },
  { icon: Clock, title: "Real-time Status", description: "Live availability updates" },
];

export function WebPortalNew() {
  return (
    <section id="portal" className="py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-card/40" />
      <div className="absolute top-0 left-0 w-full section-divider" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mb-16">
          <ScrollReveal>
            <motion.div 
              className="inline-flex items-center gap-2 badge-accent mb-6"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Driver Experience
            </motion.div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              Web <span className="text-gradient">Portal</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2} className="flex items-end">
            <p className="text-muted-foreground text-lg lg:text-xl font-light leading-relaxed">
              White-label web portal for drivers to find stations, manage sessions, and handle payments seamlessly.
            </p>
          </ScrollReveal>
        </div>

        {/* Portal Preview + Features */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Portal Mockup */}
          <ScrollReveal className="lg:col-span-7">
            <motion.div 
              className="glass-card p-4 md:p-6 rounded-3xl"
              whileHover={{ scale: 1.005 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src={dashboardWebPortal} 
                alt="EVGateway web portal dashboard showing charging statistics, chargers status, and connectors overview" 
                className="w-full object-contain rounded-2xl"
              />
            </motion.div>
          </ScrollReveal>

          {/* Features Grid */}
          <div className="lg:col-span-5">
            <StaggerContainer className="grid grid-cols-2 gap-4" staggerDelay={0.08}>
              {portalFeatures.map((feature, idx) => (
                <StaggerItem key={idx}>
                  <motion.div
                    whileHover={{ scale: 1.03, y: -4 }}
                    transition={{ duration: 0.3 }}
                    className="glass-card p-6 hover:border-accent/40 transition-all group"
                  >
                    <motion.div 
                      className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center mb-4"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <feature.icon className="h-5 w-5 text-accent" />
                    </motion.div>
                    <h3 className="font-semibold mb-1 text-white">{feature.title}</h3>
                    <p className="text-sm text-white/60 font-light">{feature.description}</p>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full section-divider" />
    </section>
  );
}